/**
 * ExperienceCard Organism
 *
 * Displays an experience/exhibition card with image, details, and actions.
 * Supports desktop dialog and mobile drawer variants.
 */

'use client';

import { useTranslation } from '@/app/i18n/client';
import { Button } from '@/components/atoms/buttons';
import { OptimizedImage } from '@/components/atoms/media';
import { formatDate } from '@/utils/date';
import { EnumExperienceType, Experience } from '@/utils/types';
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import Link from 'next/link';

type ExperienceCardProps = {
  lng: string;
  experience: Experience;
  imageClassName?: string;
  showButtons?: boolean;
  isCarousel?: boolean;
  detailsClassName?: string;
  carouselRef?: React.RefObject<HTMLDivElement | null>;
  imagePriority?: boolean;
};

export function ExperienceCard({
  lng,
  experience,
  imageClassName,
  showButtons = true,
  isCarousel = false,
  detailsClassName,
  carouselRef,
  imagePriority = false,
}: ExperienceCardProps) {
  const { t } = useTranslation(lng, ['experiences', 'tickets']);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isTop, setIsTop] = useState(false);
  const { id, name, slug, startDate, endDate, summary, description, experiencesType } = experience;

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) return;

    const onScroll = () => {
      setIsTop(window.pageYOffset === 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isDrawerOpen]);

  const getExperienceLink = () => {
    if (
      experiencesType?.name === EnumExperienceType.MASP_CURSOS ||
      experiencesType?.name === EnumExperienceType.MASP_MEDIACAO_PROFESSORES ||
      experiencesType?.name === EnumExperienceType.PRE_COLLEGE_MASP
    ) {
      return `/courses/${slug}`;
    } else if (experiencesType?.name === EnumExperienceType.MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR) {
      return `/eventos/${slug}`;
    } else {
      return `/tickets/${slug}`;
    }
  };

  const handleCutExperienceName = (name: string) => {
    if (name.length > 80) name = name.slice(0, 80) + '...';

    return name;
  };

  const desktopDialogContent = (
    <SheetContent
      className='flex flex-col gap-4 overflow-auto bg-background p-[20px] pb-40'
      style={{ width: '50vw', maxWidth: '50vw' }}>
      <SheetHeader>
        <div className='flex flex-col items-start justify-start gap-4'>
          <div className='flex flex-col gap-2'>
            <div className='mb-2 flex text-muted-foreground max-sm:mb-0'>
              <div className='flex'>
                {startDate && <p className='text-lg font-light'>{formatDate(startDate)}</p>}
                {endDate && <p className='text-lg font-light'>- {formatDate(endDate)}</p>}
              </div>
            </div>
            <h3 className='mb-[30px] text-[44pt] font-bold leading-[50pt]'>{name}</h3>
          </div>

          <OptimizedImage
            src={`${experience.onTextImages[0] || experience.image}`}
            width={960}
            height={540}
            loading='lazy'
            quality={70}
            sizes='50vw'
            className={`aspect-video h-auto w-full object-cover`}
            alt={experience.name}
          />
        </div>
      </SheetHeader>
      <div className='relative mt-4 flex flex-col'>
        <h3 className='mb-8 text-[50px] font-bold leading-10'>Em Cartaz</h3>
        <p className='whitespace-pre-wrap text-base font-light'>{summary}</p>
        <p className='whitespace-pre-wrap text-base font-light'>{description}</p>

        <div className='sticky bottom-0 left-0 right-0'>
          <div className='relative -bottom-20 mx-auto w-full max-w-[50vw]'>
            <Link href={getExperienceLink()}>
              <Button variant='default' className='h-[54px] w-full text-lg'>
                {t('buyTickets')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </SheetContent>
  );

  const mobileDrawerContent = (
    <div
      className={`fixed inset-x-0 bottom-0 ${isTop ? 'top-[100px]' : 'top-0'} z-50 flex transform flex-col gap-4 overflow-auto bg-background ${isDrawerOpen ? 'animate-drawer-up' : 'animate-drawer-down'}`}>
      <div className='flex w-full items-start justify-between'>
        <OptimizedImage
          src={`${experience.onTextImages[0] || experience.image}`}
          width={640}
          height={360}
          className='aspect-video h-auto min-h-[350px] w-full object-cover'
          quality={70}
          sizes='100vw'
          loading='lazy'
          alt={experience.name}
        />
        <button
          onClick={() => setDrawerOpen(false)}
          aria-label={t('experiences:closeDetails')}
          className='absolute right-4 top-4 z-50 cursor-pointer text-white'>
          <X width={24} aria-hidden='true' />
        </button>
      </div>
      <div className='flex flex-col gap-2 px-[10px]'>
        <div className='mb-2 flex text-muted-foreground'>
          <div className='flex'>
            {startDate && <p className='text-lg font-light'>{formatDate(startDate)}</p>}
            {endDate && <p className='text-lg font-light'>- {formatDate(endDate)}</p>}
          </div>
        </div>
        <h3 className='mb-[60px] text-[28pt] font-bold leading-[30pt] max-sm:mb-[30px]'>{name}</h3>
      </div>
      <div className='relative mx-4 flex flex-col border-t border-foreground pb-20 pt-6 max-sm:pt-2'>
        <h3 className='mb-8 text-[28px] font-bold leading-[30pt]'>Em Cartaz</h3>
        <p className='whitespace-pre-wrap'>{summary}</p>
        <p className='whitespace-pre-wrap'>{description}</p>
      </div>

      {showButtons && (
        <div className='sticky bottom-0 left-0 right-0'>
          <div className='relative bottom-10 mx-auto w-full max-w-[40vw] max-sm:max-w-[92vw]'>
            <Link href={getExperienceLink()}>
              <Button variant='default' className='h-[54px] w-full text-lg'>
                {t('buyTickets')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Sheet>
        <div
          data-testid='experience-card'
          className={`group relative mb-[80px] flex flex-col items-center justify-start overflow-hidden max-sm:mb-[70px]`}>
          <OptimizedImage
            src={experience.onTextImages[0] || experience.image}
            width={1920}
            height={1080}
            quality={70}
            priority={imagePriority}
            loading={imagePriority ? undefined : 'lazy'}
            fetchPriority={imagePriority ? 'high' : undefined}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 45vw'
            className={cn(`aspect-[4/3] h-full max-h-[712px] w-full object-cover`, imageClassName)}
            alt={experience.name}
          />

          <div className='min-md:h-44 flex w-full items-start justify-between max-lg:flex-col'>
            {!isCarousel && (
              <div className='flex w-full flex-col items-start justify-between'>
                {endDate && (
                  <p className='mt-2 text-lg font-light text-muted-foreground'>
                    {formatDate(startDate)} - {formatDate(endDate)}
                  </p>
                )}

                <h3
                  data-testid='experience-card-title'
                  className='mt-2 h-full pr-2 text-xl font-medium max-[1450px]:text-lg max-md:text-lg'>
                  {handleCutExperienceName(name)}
                </h3>

                {showButtons && (
                  <>
                    <SheetTrigger asChild className='hidden md:flex'>
                      <Button
                        data-testid='experience-card-details-button'
                        variant='link'
                        className='mt-[12px] px-0 text-lg font-light capitalize text-foreground hover:text-primary'>
                        {t('seeDetails')}
                        <Plus className='ml-2 h-4 w-4' />
                      </Button>
                    </SheetTrigger>

                    {setDrawerOpen && (
                      <Button
                        data-testid='experience-card-details-button'
                        onClick={() => setDrawerOpen(true)}
                        className='mt-2 px-0 text-lg font-light capitalize text-foreground hover:text-primary md:hidden'
                        variant='link'>
                        {t('seeDetails')}
                        <Plus className='ml-2 h-5 w-5' />
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {isCarousel && (
              <div className={`flex w-full flex-col items-start justify-between ${detailsClassName}`}>
                <h3 className='mt-2 h-full pr-2 text-xl font-medium max-[1450px]:text-lg max-md:text-lg'>
                  {handleCutExperienceName(name)}
                </h3>
                <span className='text-lg'>
                  {formatDate(startDate)} - {formatDate(endDate)}
                </span>
              </div>
            )}

            {showButtons && (
              <div className='relative max-lg:bottom-0 max-lg:mt-10 max-lg:w-full max-sm:mt-8 lg:-bottom-5'>
                <Link href={getExperienceLink()}>
                  <Button
                    data-testid='experience-card-buy-button'
                    variant='outline'
                    className='min-h-[54px] min-w-[197px] px-4 py-3 text-lg text-foreground max-lg:w-full'>
                    {t('tickets:tickets')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {desktopDialogContent}
      </Sheet>

      {isDrawerOpen && mobileDrawerContent}
    </>
  );
}

export default ExperienceCard;
